/* section-image-with-text.js
   Custom play/pause + sound controls for the hosted video in the
   "Image with text" section. Native <video controls> stays off; this
   element owns the whole control surface.

   States (classes on the host element, consumed by the CSS):
     .is-idle     — nothing has played yet; centre play button showing
     .is-playing  — video is running
     .is-unmuted  — audio is on

   Autoplay mode plays muted when the video scrolls into view and pauses
   when it leaves. prefers-reduced-motion downgrades to click-to-play.
*/
(function () {
  if (customElements.get('image-with-text-video')) return;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ImageWithTextVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.image-with-text__video-el');
      if (!this.video) return;

      this.playBtn = this.querySelector('[data-iwt-play]');
      this.muteBtn = this.querySelector('[data-iwt-mute]');
      this.bigPlayBtn = this.querySelector('[data-iwt-bigplay]');

      this.autoplay = this.dataset.autoplay === 'true' && !REDUCED_MOTION.matches;
      this.hasPlayed = false;

      // Belt and braces: iOS refuses inline playback without these, and the
      // theme editor can re-render the markup without them.
      this.video.setAttribute('playsinline', '');
      this.video.controls = false;
      this.video.muted = this.autoplay;

      this.onPlay = this.setState.bind(this);
      this.onPause = this.setState.bind(this);
      this.onVolume = this.setState.bind(this);
      this.onTogglePlay = this.togglePlay.bind(this);
      this.onToggleMute = this.toggleMute.bind(this);

      this.video.addEventListener('play', this.onPlay);
      this.video.addEventListener('pause', this.onPause);
      this.video.addEventListener('volumechange', this.onVolume);
      if (this.playBtn) this.playBtn.addEventListener('click', this.onTogglePlay);
      if (this.bigPlayBtn) this.bigPlayBtn.addEventListener('click', this.onTogglePlay);
      if (this.muteBtn) this.muteBtn.addEventListener('click', this.onToggleMute);

      if (this.autoplay) {
        this.classList.remove('is-idle');
        this.observe();
      } else {
        this.classList.add('is-idle');
      }

      this.setState();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (!this.video) return;
      this.video.removeEventListener('play', this.onPlay);
      this.video.removeEventListener('pause', this.onPause);
      this.video.removeEventListener('volumechange', this.onVolume);
      if (this.playBtn) this.playBtn.removeEventListener('click', this.onTogglePlay);
      if (this.bigPlayBtn) this.bigPlayBtn.removeEventListener('click', this.onTogglePlay);
      if (this.muteBtn) this.muteBtn.removeEventListener('click', this.onToggleMute);
    }

    /* Play only while on screen; pause off screen so a background video never
       burns battery or bandwidth. A manual pause opts out for good. */
    observe() {
      if (!('IntersectionObserver' in window)) {
        this.play();
        return;
      }

      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (entry) {
              if (entry.isIntersecting) {
                if (!this.userPaused) this.play();
              } else if (!this.video.paused) {
                this.video.pause();
              }
            }.bind(this)
          );
        }.bind(this),
        { threshold: 0.25 }
      );

      this.observer.observe(this);
    }

    play() {
      var promise = this.video.play();
      if (promise && typeof promise.catch === 'function') {
        // Autoplay can still be refused (low-power mode, data saver);
        // fall back to the poster + centre play button.
        promise.catch(
          function () {
            this.classList.add('is-idle');
            this.setState();
          }.bind(this)
        );
      }
    }

    togglePlay() {
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    toggleMute() {
      this.video.muted = !this.video.muted;
      // Unmuting a never-played video should start it — that is what the
      // sound button implies to a visitor.
      if (!this.video.muted && this.video.paused) {
        this.userPaused = false;
        this.play();
      }
    }

    setState() {
      var playing = !this.video.paused && !this.video.ended;
      var unmuted = !this.video.muted;

      if (playing) {
        this.hasPlayed = true;
        this.classList.remove('is-idle');
      } else if (!this.hasPlayed) {
        this.classList.add('is-idle');
      }

      this.classList.toggle('is-playing', playing);
      this.classList.toggle('is-unmuted', unmuted);

      if (this.playBtn) {
        this.playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      }
      if (this.muteBtn) {
        this.muteBtn.setAttribute('aria-pressed', String(!unmuted));
        this.muteBtn.setAttribute('aria-label', unmuted ? 'Mute video' : 'Unmute video');
      }
    }
  }

  customElements.define('image-with-text-video', ImageWithTextVideo);
})();
